import submitContactForm from "@carotek/Handlers/ContactUsForm/SubmitContactUsForm";
import CarotekColors from "@carotek/utils/Colors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AccountModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    loadCurrentCountries,
    setFieldValue,
    submitContactForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),
};

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
    countries: getCurrentCountries(state),
    accountSettings: getSettingsCollection(state).accountSettings,
    reCaptchaEnabledForCreateAccount: getSettingsCollection(state).websiteSettings.reCaptchaEnabledForCreateAccount,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    userGridContainer?: GridContainerProps;
    formWrapper?: GridItemProps;
    formFieldsGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    activationInstructionGridItem?: GridItemProps;
    activationInstructionText?: TypographyPresentationProps;
    activationSuccessWrapper?: InjectableCss;
    activationSuccessTitleText?: TypographyPresentationProps;
    activationSuccessMessageText?: TypographyPresentationProps;
    activationSuccessHomeButton?: ButtonPresentationProps;
    outSideUsContainer?: GridItemProps;
    requiredGridItem?: GridItemProps;
}

export const createAccountStyles: CreateAccountStyles = {
    userGridContainer: {
        offsetCss: css`
            background: #fff;
            width: 100% !important;
            margin: 0 !important;
        `,
        css: css`
            background: #f3f3f4;
            border: 1px solid ${CarotekColors.grey5};
            padding: 46px 40px 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 46px 25px 50px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    formWrapper: {
        width: 12,
        css: css`
            padding: 20px 56px 20px 45px;
            flex-direction: column;
            row-gap: 10px;
            [class^="GridItemStyle"] {
                padding: 0;
                margin-bottom: 10px;
                width: 100%;

                textarea {
                    resize: none;
                    min-height: 80px;
                }

                [class^="FormFieldStyle"] {
                    label {
                        font-size: 16px;

                        span {
                            color: ${CarotekColors.red2};
                        }
                    }
                }
            }
        `,
    },
    formFieldsGridItem: {
        width: 12,
        css: css`
            [class^="TypographyStyle"] {
                width: 50%;
            }
            @media only screen and (max-width: 767px) {
                [class^="TypographyStyle"] {
                    width: 100%;
                }
            }
            [class^="FormFieldStyle"] {
                margin-bottom: 16px;
                flex-direction: row;
                @media only screen and (max-width: 767px) {
                    flex-direction: column;
                    [class^="FormInputWrapper"] {
                        width: 100% !important;
                    }
                }
                [class^="FormInputWrapper"] {
                    width: 90%;
                }
            }
        `,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            div[data-test-selector="createAccount_Subscription"] {
                flex-direction: row-reverse;
                gap: 10px;
                label {
                    margin: 0 !important;
                }
            }
        `,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            margin-bottom: 30px;
            button {
                padding: 10px 17px;
                [class^="TypographyStyle"] {
                    font-size: 14px;
                }
                :hover {
                    background-color: ${getColor("danger")};
                    border-color: ${getColor("danger")};
                }
            }
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            font-size: 16px;
            padding-top: 0;
        `,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
        size: 22,
        weight: 700,
        color: "text.main",
        css: css`
            width: 100%;
            text-align: center;
            margin-top: 30px;
        `,
    },
    activationInstructionGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    activationSuccessWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 50%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    activationSuccessTitleText: {
        variant: "h3",
    },
    activationSuccessHomeButton: {
        css: css`
            align-self: flex-end;
        `,
    },
    outSideUsContainer: {
        width: 12,
        css: css`
            margin-bottom: 0 !important;
            [class^="CheckboxStyle"] {
                flex-direction: row-reverse;

                label {
                    margin-right: 10px;
                    margin-left: 0 !important;
                }
            }
        `,
    },
    requiredGridItem: {
        width: 12,
        css: css`
            margin-bottom: 0 !important;
            justify-content: flex-end;
            padding: 0;
            background: #f3f3f4;
            span {
                font-size: 13px;
            }
            .aestrick {
                color: ${CarotekColors.red2};
                margin-right: 5px;
            }
        `,
    },
    submitButton: {},
};

const styles = createAccountStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");

const CreateAccount = ({
    loadCurrentCountries,
    countries,
    setFieldValue,
    validateReCaptcha,
    submitContactForm,
    accountSettings,
    history,
    location,
}: Props) => {
    const firstNameRequiredFieldMessage = siteMessage("First Name is required");
    const lastNameRequiredFieldMessage = siteMessage("Last Name is required");
    const titleRequiredFieldMessage = siteMessage("Title is required");
    const companyRequiredFieldMessage = siteMessage("Company is required");
    const streetRequiredFieldMessage = siteMessage("Street is required");
    const cityRequiredFieldMessage = siteMessage("City is required");
    const emailRequiredFieldMessage = siteMessage("Email is Required");
    const stateRequiredFieldMessage = siteMessage("State is required");
    const zipCodeRequiredFieldMessage = siteMessage("Zip Code is required");
    const notesRequiredFieldMessage = siteMessage("Notes is Required");
    const areaOfInterestRequiredFieldMessage = siteMessage("Area of Interest is Required");
    const industryRequiredFieldMessage = siteMessage("Industry is Required");

    const [errorMessage, setErrorMessage] = useState("");
    const [isSubscribed, setIsSubscribed] = useState(true);
    const [outSideUS, setOutSideUS] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef<ReCAPTCHA>();
    const [allStates, setAllStates] = useState<any>();

    const [userFirstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState(firstNameRequiredFieldMessage);

    const [userLastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState(lastNameRequiredFieldMessage);

    const [userTitle, setTitle] = useState("");
    const [titleError, setTitleError] = useState(titleRequiredFieldMessage);

    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState(companyRequiredFieldMessage);

    const [street, setStreet] = useState("");
    const [streetError, setStreetError] = useState(streetRequiredFieldMessage);

    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState(cityRequiredFieldMessage);

    const [state, setState] = useState("");
    const [stateError, setStateError] = useState(stateRequiredFieldMessage);

    const [areaOfInterest, setAreaOfInterest] = useState("");
    const [areaOfInterestError, setAreaOfInterestError] = useState(areaOfInterestRequiredFieldMessage);

    const [industry, setIndustry] = useState("");
    const [industryError, setIndustryError] = useState(industryRequiredFieldMessage);

    const [zip, setZip] = useState("");
    const [zipCodeError, setZipCodeError] = useState(zipCodeRequiredFieldMessage);

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);

    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [phoneRequiredError, setPhoneRequiredError] = useState("Phone Number is Required");

    const [mobile, setMobile] = useState("");
    const [mobileError, setMobileError] = useState("");
    const [mobileRequiredError, setMobileRequiredError] = useState("Mobile is Required");

    const [notes, setNotes] = useState("");
    const [notesError, setNotesError] = useState(notesRequiredFieldMessage);

    const isNewsletterPage = location.pathname.includes("/newsletter");
    const isLineCardPage = location.pathname.includes("/line-card-request");
    const formType = isNewsletterPage ? "newslettersignup" : "contactus";

    React.useEffect(() => {
        loadCurrentCountries();
    }, []);

    React.useEffect(() => {
        if (countries) {
            const getStates = countries?.find((option: any) => option.name === "United States");
            setAllStates(getStates?.states);
        }
    }, [countries]);

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        useEmailAsUserName,
        requireActivateAccount,
    } = accountSettings;

    const addDashToPhone = (value: string) => {
        if (value.length > 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
        }
        if (value.length > 3 && value.length <= 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}`;
        }
        return value;
    };

    const removeDashFromPhone = (value: string) => {
        return value.replace(/\D/g, "");
    };

    const validateFirstName = (name: string) => {
        const errorMessage = !name ? firstNameRequiredFieldMessage : "";
        setFirstNameError(errorMessage);
        return !errorMessage;
    };
    const validateLastName = (value: string) => {
        const errorMessage = !value ? lastNameRequiredFieldMessage : "";
        setLastNameError(errorMessage);
        return !errorMessage;
    };
    const validateTitle = (value: string) => {
        const errorMessage = !value ? titleRequiredFieldMessage : "";
        setTitleError(errorMessage);
        return !errorMessage;
    };
    const validateCompany = (value: string) => {
        const errorMessage = !value ? companyRequiredFieldMessage : "";
        setCompanyError(errorMessage);
        return !errorMessage;
    };
    const validateStreet = (value: string) => {
        const errorMessage = !value ? streetRequiredFieldMessage : "";
        setStreetError(errorMessage);
        return !errorMessage;
    };
    const validateCity = (value: string) => {
        const errorMessage = !value ? cityRequiredFieldMessage : "";
        setCityError(errorMessage);
        return !errorMessage;
    };
    const validateState = (value: string) => {
        const errorMessage = !value ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };
    const validateAreaOfInterest = (value: string) => {
        const errorMessage = !value ? areaOfInterestRequiredFieldMessage : "";
        setAreaOfInterestError(errorMessage);
        return !errorMessage;
    };
    const validateIndustry = (value: string) => {
        const errorMessage = !value ? industryRequiredFieldMessage : "";
        setIndustryError(errorMessage);
        return !errorMessage;
    };
    const validateZipCode = (name: string) => {
        const zipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
        const errorMessage = !name ? zipCodeRequiredFieldMessage : !zipRegex.test(name) ? "Enter Valid Zip Code" : "";
        setZipCodeError(errorMessage);
        return !errorMessage;
    };
    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : "Email is not valid";
        setEmailError(errorMessage);
        return !errorMessage;
    };
    const validateNotes = (value: string) => {
        const errorMessage = !value ? notesRequiredFieldMessage : "";
        setNotesError(errorMessage);
        return !errorMessage;
    };
    const validateSubmitEnabled = () => {
        if (isNewsletterPage) {
            return !(
                firstNameError ||
                lastNameError ||
                companyError ||
                emailError ||
                stateError ||
                areaOfInterestError
            );
        }

        return !(
            firstNameError ||
            lastNameError ||
            titleError ||
            companyError ||
            streetError ||
            cityError ||
            emailError ||
            stateError ||
            zipCodeError ||
            notesError ||
            industryError ||
            areaOfInterestError ||
            phoneError ||
            mobileError ||
            phoneRequiredError ||
            mobileRequiredError
        );
    };

    const firstNameChangeHandler = (value: string) => {
        validateFirstName(value);
        setFieldValue({ key: "firstName", value });
        setFirstName(value);
    };
    const lastNameChangeHandler = (value: string) => {
        validateLastName(value);
        setFieldValue({ key: "lastName", value });
        setLastName(value);
    };
    const titleChangeHandler = (value: string) => {
        validateTitle(value);
        setFieldValue({ key: "title", value });
        setTitle(value);
    };
    const companyChangeHandler = (value: string) => {
        validateCompany(value);
        setFieldValue({ key: "companyName", value });
        setCompany(value);
    };
    const streetChangeHandler = (value: string) => {
        validateStreet(value);
        setFieldValue({ key: "street", value });
        setStreet(value);
    };
    const cityChangeHandler = (value: string) => {
        validateCity(value);
        setFieldValue({ key: "city", value });
        setCity(value);
    };
    const stateChangeHandler = (value: string) => {
        validateState(value);
        setFieldValue({ key: "state", value });
        setState(value);
    };
    const areaOfInterestChangeHandler = (value: string) => {
        validateAreaOfInterest(value);
        setFieldValue({ key: "areaOfInterest", value });
        setAreaOfInterest(value);
    };
    const industryChangeHandler = (value: string) => {
        validateIndustry(value);
        setFieldValue({ key: "industry", value });
        setIndustry(value);
    };
    const zipChangeHandler = (value: string) => {
        validateZipCode(value);
        setZip(value);
        setFieldValue({ key: "zipcodeName", value });
    };
    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setFieldValue({ key: "emailAddress", value: email });
        setEmail(email);
    };
    const phoneHandler = (value: string) => {
        const regex = /[0-9]|\./;
        if (!value) {
            setPhone("");
            setPhoneRequiredError("Phone Number is Required");
            setFieldValue({ key: "phoneNumber", value: "" });
            return;
        }
        setPhoneRequiredError("");
        value = removeDashFromPhone(value);
        if (!regex.test(value) || value.length !== 10) {
            setPhone(value);
            setPhoneError("Phone Number is not Valid");
        } else {
            setPhoneError("");
        }
        if (value) {
            const formattedPhone = addDashToPhone(value);
            formattedPhone !== undefined && setPhone(formattedPhone);
            setFieldValue({ key: "phoneNumber", value: formattedPhone });
        }
    };
    const mobileHandler = (value: string) => {
        const regex = /[0-9]|\./;
        if (!value) {
            setMobile("");
            setMobileRequiredError("Mobile Number is Required");
            setFieldValue({ key: "mobileNumber", value: "" });
            return;
        }
        setMobileRequiredError("");
        value = removeDashFromPhone(value);
        if (!regex.test(value) || value.length !== 10) {
            setMobile(value);
            setMobileError("Mobile Number is not Valid");
        } else {
            setMobileError("");
        }
        if (value) {
            const formattedPhone = addDashToPhone(value);
            formattedPhone !== undefined && setMobile(formattedPhone);
            setFieldValue({ key: "mobileNumber", value: formattedPhone });
        }
    };
    const notesChangeHandler = (value: string) => {
        validateNotes(value);
        setFieldValue({ key: "notes", value });
        setNotes(value);
    };

    const clearFormFields = () => {
        setFirstName("");
        setLastName("");
        setTitle("");
        setCompany("");
        setStreet("");
        setCity("");
        setState("");
        setZip("");
        setPhone("");
        setMobile("");
        setEmail("");
        setNotes("");
        setIndustry("");
        setAreaOfInterest("");
        setErrorMessage("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setFirstNameError(firstNameRequiredFieldMessage);
        setLastNameError(lastNameRequiredFieldMessage);
        setTitleError(titleRequiredFieldMessage);
        setCompanyError(companyRequiredFieldMessage);
        setStreetError(streetRequiredFieldMessage);
        setCityError(cityRequiredFieldMessage);
        setEmailError(emailRequiredFieldMessage);
        setStateError(stateRequiredFieldMessage);
        setZipCodeError(zipCodeRequiredFieldMessage);
        setPhoneError("");
        setMobileError("");
        setPhoneRequiredError("Phone Number is Required");
        setMobileRequiredError("Mobile Number is Required");
        setNotesError(notesRequiredFieldMessage);
        setIndustryError(industryRequiredFieldMessage);
        setAreaOfInterestError(areaOfInterestRequiredFieldMessage);
    };

    const onSuccessHandler = (account?: AccountModel) => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Success"), messageType: "success" });
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        const emailRecipients = "";
        setFieldValue({ key: "isEmailNewsletterOptIn", value: `${isSubscribed}` });
        setFieldValue({ key: "isOutsideUSUser", value: `${outSideUS}` });
        setFieldValue({ key: "formType", value: formType });
        submitContactForm({
            emailRecipients,
            onSuccess: () => {
                setErrorMessage("");
                clearFormFields();

                if (isLineCardPage) {
                    history.push("/line-card-request-thanks");
                    setIsSubmitting(false);
                    return;
                }

                function getDataLayer() {
                    return (window as any).dataLayer;
                }
                const dataLayer = getDataLayer();
                if (dataLayer) {
                    if (isNewsletterPage) {
                        history.push("/general-thanks/form-general-thanks");
                        dataLayer.push({ ecommerce: null });
                        dataLayer.push({
                            event: "contact_us_form_submit",
                            // page_path: window.location.pathname,
                        });
                    } else {
                        history.push("/general-thanks");
                        dataLayer.push({ ecommerce: null });
                        dataLayer.push({
                            event: "contact_us_form_submit",
                            // page_path: window.location.pathname,
                        });
                    }
                }
                setIsSubmitting(false);
            },
            onComplete() {},
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.userGridContainer}>
                <GridItem {...styles.requiredGridItem}>
                    <Typography as="span">
                        <span className="aestrick">*</span> Indicates required field
                    </Typography>
                </GridItem>
                <GridItem {...styles.formWrapper}>
                    {!isNewsletterPage && (
                        <GridItem {...styles.outSideUsContainer}>
                            <CheckboxGroup>
                                <Checkbox
                                    onChange={(e, value) => {
                                        if (value) {
                                            // eslint-disable-next-line no-alert
                                            alert(
                                                "We do not accept international inquiries for products, please contact the product manufacturer to find a distributor in your country",
                                            );
                                        }
                                        setOutSideUS(value);
                                    }}
                                    checked={outSideUS}
                                    data-test-selector="createAccount_Subscription"
                                >
                                    {translate("Outside the US?")}:
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                    )}

                    <GridItem {...styles.formFieldsGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            type="text"
                            placeholder={translate("First Name")}
                            label={translate("First Name")}
                            onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                            onBlur={e => validateFirstName(e.currentTarget.value)}
                            value={userFirstName}
                            data-test-selector="createAccount_firstName"
                            maxLength={256}
                            required
                            error={isSubmitted && firstNameError}
                        />
                    </GridItem>

                    <GridItem {...styles.formFieldsGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            type="text"
                            placeholder={translate("Last Name")}
                            label={translate("Last Name")}
                            onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                            value={userLastName}
                            data-test-selector="createAccount_lasttName"
                            maxLength={256}
                            required
                            error={isSubmitted && lastNameError}
                        />
                    </GridItem>

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="text"
                                placeholder={translate("Title")}
                                label={translate("Title")}
                                onChange={e => titleChangeHandler(e.currentTarget.value)}
                                value={userTitle}
                                data-test-selector="createAccount_title"
                                maxLength={256}
                                required
                                error={isSubmitted && titleError}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.formFieldsGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            type="text"
                            placeholder={translate("Company")}
                            label={translate("Company")}
                            onChange={e => companyChangeHandler(e.currentTarget.value)}
                            value={company}
                            data-test-selector="createAccount_Company"
                            maxLength={256}
                            required
                            error={isSubmitted && companyError}
                        />
                    </GridItem>

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="text"
                                placeholder={translate("Street")}
                                label={translate("Street")}
                                onChange={e => streetChangeHandler(e.currentTarget.value)}
                                value={street}
                                data-test-selector="createAccount_StreetAddress"
                                maxLength={256}
                                required
                                error={isSubmitted && streetError}
                            />
                        </GridItem>
                    )}

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="text"
                                placeholder={translate("City")}
                                label={translate("City")}
                                onChange={e => cityChangeHandler(e.currentTarget.value)}
                                value={city}
                                data-test-selector="createAccount_City"
                                maxLength={256}
                                required
                                error={isSubmitted && cityError}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.formFieldsGridItem}>
                        <Select
                            label={translate("State")}
                            value={state}
                            required
                            error={isSubmitted && stateError}
                            onChange={e => stateChangeHandler(e.currentTarget.value)}
                        >
                            <option value="">Select State</option>
                            {allStates?.map((state: any, index: number) => {
                                return (
                                    <option value={state.name} key={index}>
                                        {state.name}
                                    </option>
                                );
                            })}
                        </Select>
                    </GridItem>

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="text"
                                placeholder={translate("ZipCode")}
                                label={translate("Zip")}
                                onChange={e => zipChangeHandler(e.currentTarget.value)}
                                value={zip}
                                data-test-selector="createAccount_ZipCode"
                                maxLength={256}
                                required
                                error={isSubmitted && zipCodeError}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.formFieldsGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            type="email"
                            placeholder={translate("Email Address")}
                            label={translate("Email")}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            value={email}
                            data-test-selector="createAccount_email"
                            maxLength={256}
                            required
                            error={isSubmitted && emailError}
                        />
                    </GridItem>

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="text"
                                placeholder={translate("PhoneNo")}
                                label={translate("Phone")}
                                onChange={e => phoneHandler(e.currentTarget.value)}
                                value={phone}
                                data-test-selector="createAccount_phone"
                                maxLength={256}
                                required
                                error={(isSubmitted && phoneRequiredError) || phoneError}
                            />
                        </GridItem>
                    )}

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="text"
                                placeholder={translate("MobileNo")}
                                label={translate("Mobile")}
                                onChange={e => mobileHandler(e.currentTarget.value)}
                                value={mobile}
                                data-test-selector="createAccount_Mobile"
                                maxLength={256}
                                required
                                error={(isSubmitted && mobileRequiredError) || mobileError}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.formFieldsGridItem}>
                        <Select
                            required
                            label={translate("Area Of Interest")}
                            value={areaOfInterest}
                            error={isSubmitted && areaOfInterestError}
                            onChange={e => areaOfInterestChangeHandler(e.currentTarget.value)}
                        >
                            <option value="">None</option>
                            <option value="Control Panels">Control Panels</option>
                            <option value="Instruments">Instruments</option>
                            <option value="Municipal">Municipal</option>
                            <option value="Process Skids">Process Skids</option>
                            <option value="Process Solution Summit">Process Solution Summit</option>
                            <option value="Pumps, Blowers & Heat Exchangers">Pumps, Blowers & Heat Exchangers</option>
                            <option value="Seal Pots">Seal Pots</option>
                            <option value="Services - Calibration, Field Service, Pump and Blower Repair">
                                Services - Calibration, Field Service, Pump and Blower Repair
                            </option>
                            <option value="Steam, Air & Hydronic">Steam, Air & Hydronic</option>
                            <option value="Training">Training</option>
                            <option value="Valves">Valves</option>
                            <option value="Others">Others</option>
                        </Select>
                    </GridItem>

                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <Select
                                required
                                label={translate("Industry")}
                                value={industry}
                                error={isSubmitted && industryError}
                                onChange={e => industryChangeHandler(e.currentTarget.value)}
                            >
                                <option value="">None</option>
                                <option value="Adhesives">Adhesives</option>
                                <option value="Biotechnology">Biotechnology</option>
                                <option value="Chemicals">Chemicals</option>
                                <option value="Energy">Energy</option>
                                <option value="Engineering">Engineering</option>
                                <option value="Food & Beverage">Food & Beverage</option>
                                <option value="Government">Government</option>
                                <option value="Industrial Manufacturing">Industrial Manufacturing</option>
                                <option value="Institutional">Institutional</option>
                                <option value="Machinery">Machinery</option>
                                <option value="Manufacturing">Manufacturing</option>
                                <option value="Oil & Gas">Oil & Gas</option>
                                <option value="Paint & Solvents">Paint & Solvents</option>
                                <option value="Personal Care">Personal Care</option>
                                <option value="Pharmaceutical">Pharmaceutical</option>
                                <option value="Plastics">Plastics</option>
                                <option value="Pulp & Paper">Pulp & Paper</option>
                                <option value="Utilities">Utilities</option>
                                <option value="Water-Wastewater">Water-Wastewater</option>
                                <option value="Others">Others</option>
                            </Select>
                        </GridItem>
                    )}

                    {!isNewsletterPage && (
                        <GridItem {...styles.subscriptionGridItem}>
                            <CheckboxGroup>
                                <Checkbox
                                    onChange={(e, value) => setIsSubscribed(value)}
                                    checked={isSubscribed}
                                    data-test-selector="createAccount_Subscription"
                                >
                                    {translate("Email Newsletter opt in:")}
                                </Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                    )}
                    {!isNewsletterPage && (
                        <GridItem {...styles.formFieldsGridItem}>
                            <TextArea
                                label={translate("Notes")}
                                placeholder={translate("Notes")}
                                required
                                onChange={e => notesChangeHandler(e.currentTarget.value)}
                                value={notes}
                                error={isSubmitted && notesError}
                            ></TextArea>
                        </GridItem>
                    )}

                    {errorMessage && (
                        <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                            <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                        </GridItem>
                    )}

                    <GridItem {...styles.reCaptchaGridItem}>
                        <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                    </GridItem>

                    <GridItem {...styles.submitGridItem}>
                        <Button
                            {...styles.submitButton}
                            type="submit"
                            disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                            data-test-selector="createAccount_createButton"
                        >
                            {translate("Submit")}
                        </Button>
                    </GridItem>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CreateAccount)),
    definition: {
        displayName: "Contact Us Form",
        group: "Contact Us",
    },
};

export default widgetModule;
